import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	StaticLookup,
	isEmpty,
	getSrchTokens,
	matchSrchTokens,
	setPreference,
	getPreference
} from 'ps-gui';
import { EMPTY_LOOKUP } from '../data/lookup';
import { parseRoleId } from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('Dashboard');
const EMPTY_SET = Immutable.Set([]);
const EMPTY_LIST = Immutable.List([]);

/*
 * Zpracuje summary vraceny webovou sluzbou
 * - v tuto chvili pouze vytvori attribut totalNettoReward, kteremu priradi hodnotu z totalNetto
 * todo: pokud se nevrati potreba se summary delat neco slozitejsiho, zrusime tuto funkci a upravime webovou sluzbu, aby rovnou
 * vracela spravny nazev attributu
 */
function processSummary(value) {
	let res = Object.assign({}, value);
	res.totalNettoReward = res.totalNetto;
	/*if (res.totalNetto) {
		res.totalNettoReward = formatNumber(res.totalNetto) + ' ' + getPhrase('czk');
	}*/
	return res;
}

/*
 * mnozina accountCategoryId, pro ktere zobrazujeme jen rootovy zaznam
 */
const rootOnlyCats = {
	IND_USAGE: true,
	PERSONAL_VIDEO: true,
	PERSONAL_AUDIO: true,
	EXTRA_REWARD: true
};

/*
 * pomocna funkce pro processRows
 * zjistuje, zda zaznam ma, nebo nema byt ignorovan (ignorovany zaznam nebude zobrazen - zahodi se)
 * pravidlo pro ignorovani je v tuto chvili jedno - pokud se jedna o zanoreny zaznam (level > 1 (nebo-li parentId je nastaven)) a
 * accountCategoryId je jedna z rootOnlyCats, zaznam ignorujeme.
 */
function isIgnored(row) {
	const catId = row.accountCategoryId;
	const parentId = row.parentId;
	if (parentId) {
		if (rootOnlyCats[catId]) {
			return true;
		}
	}
	return false;
}

/*
 * pomocna funkce pro processRows
 * - odstrani children, pokud alespon jedno z nich nema vyplnenu categoryName
 */
function clearEmptyChildren(row) {
	if (row.children && row.children.length > 0) {
		if (!row.children.some(c => !!c.categoryName)) {
			row.children = null;
		} else {
			row.children.forEach(c => clearEmptyChildren(c));
		}
	}
}

/*
 * pomocna funkce pro processRows
 * 1) doplni do row vyhledavaci retezec (viz getSrchTokens)
 * 2) pro kategorii SALE doplni do levelu 2 nazev kategorie "Radioservis", pokud byl nazev kategorie prazdny
 */
function enhanceRow(row) {
	row.categoryNameSrch = getSrchTokens(row.categoryName).join('');

	if (row.lineLevel === 2) {
		if (row.accountCategoryId === 'SALE') {
			if (!row.categoryName) {
				row.categoryName = 'Radioservis';
			}
		}
	}
}

/*
 * Zpracuje zaznamy pro Dashboard vracene webovou sluzbou (tedy uvede je do formy pouzitelne modulem Dashboard)
 * - nejdulezitejsim ukolem teto funkce je z flat pole zaznamu udelat hierarchii, jelikoz zaznamy se zobrazuji ve strome
 * - mimo to dela dalsi upravy dat -> viz samotny kod
 */
function processRows(rows) {
	let rowMap = {};
	let res = [];
	const addEffectivePeriodId = (row, value) => {
		let effectivePeriodId = row.effectivePeriodId || [];
		if (effectivePeriodId.indexOf(value) < 0) {
			effectivePeriodId.push(value);
			row.effectivePeriodId = effectivePeriodId;
		}
		const parent = row.parentId ? rowMap[row.parentId] : null;
		if (parent) {
			addEffectivePeriodId(parent, value);
		}
	};

	for (let i = 0; i < rows.length; i++) {
		let row = rows[i];
		if (isIgnored(row)) {
			continue;
		}
		if (rowMap[row.id]) {
			continue;
		}
		row.naturalOrder = i;
		enhanceRow(row);

		let parent = row.parentId ? rowMap[row.parentId] : null;
		if (parent) {
			parent.children = parent.children || [];
			parent.children.push(row);
			if (row.effectivePeriodId) {
				addEffectivePeriodId(parent, row.effectivePeriodId);
			}
		} else {
			res.push(row);
		}
		if (typeof row.effectivePeriodId === 'string') {
			row.effectivePeriodId = [row.effectivePeriodId];
		}
		rowMap[row.id] = row;
	}

	// odstranime childy NP, kde neni vice eff period
	for (let i = 0; i < res.length; i++) {
		let row = res[i];
		if (row.distType === 'NP' && (!row.effectivePeriodId || row.effectivePeriodId.length <= 1)) {
			row.children = null;
		}
	}
	res.forEach(row => clearEmptyChildren(row));
	return res;
}

/**
 * Class DashboardModel - model pro modul Přehled
 */
class DashboardModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new DashboardModel(this.#data.setIn(name, value));
		} else {
			return new DashboardModel(this.#data.set(name, value));
		}

	}

	#getPreferenceKeyBase() {
		return '//dashboard//';
	}

	#getFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'filter';
	}

	#getOrderByPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'orderBy';
	}

	#getRowFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'rowFilter';
	}

	/**
	 * kontroluje, zda se nezmenil jazyk, a pokud ano, nacte znovu data, aby byla v novem jazyce.
	 * volano z modulu pri componentDidUpdate a v pripade, ze se zmenil GlobalContext, tak i componentDidUpdate
	 *
	 * @param globalContext {GlobalContextModel}
	 */
	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	/**
	 *
	 * @returns {number} cas posledni zmeny jazyka zachycene timto modelem
	 */
	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	/**
	 * expanduje/collapsuje node (zaznam) se zadanym id
	 * @param {string} id id zaznamu, ktery chceme expandovat/collapsovat
	 */
	toggleExpandedRow(id) {
		let expandedRows = this.#data.get('expandedRows') || EMPTY_SET;
		if (expandedRows.has(id)) {
			expandedRows = expandedRows.delete(id);
		} else {
			expandedRows = expandedRows.add(id);
		}
		return this.#setProperty('expandedRows', expandedRows);
	}

	/*
	 * interni pomocna funkce, ktera slouzi k inicializaci lokalnich lookupu pro dashboard
	 * - tyto lookupy jsou:
	 * 1) roleLookup - seznam roli, mezi kterymi si uzivatel muze vybirat
	 * 2) distributionLookup - pro kazdou roli je pak definovan lookup distribucnich obdobi, ktere jsou pro ni k dispozici.
	 */
	async initializeLookups() {
		let pi = await webapi.execute('/api/proc/api_dashboard/getpartyinfo');
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		// console.log('pi = ', pi);
		let model = this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(pi.roles || [])));
		if (pi.distributionPeriods) {
			for (let roleId in pi.distributionPeriods) {
				let items = pi.distributionPeriods[roleId] || [];
				model = model.#setProperty('distributionPeriodLookup.' + roleId, StaticLookup(Immutable.fromJS(items)));
			}
		}

		const roleId = pi.roles?.[0]?.key;
		return model
			.setRoleId(roleId);
	}

	initializePreferences() {
		let filter = getPreference(this.#getFilterPreferenceKey());
		let rowFilter = getPreference(this.#getRowFilterPreferenceKey());
		let orderBy = getPreference(this.#getOrderByPreferenceKey());
		let data = this.#data;
		if (filter) {
			data = data.set('filter', filter);
		}
		if (orderBy) {
			data = data.set('sortColumn', orderBy.get('sortColumn')).set('sortDesc', orderBy.get('sortDesc'));
		}
		if (rowFilter) {
			let roleLookup = data.get('roleLookup');
			if (roleLookup) {
				const roleId = rowFilter.get('roleId');
				if (roleId && roleLookup.getText(roleId)) {
					data = data.set('roleId', roleId);
				}
				const dpLookup = data.get('distributionPeriodLookup.' + data.get('roleId'));
				if (dpLookup) {
					if (rowFilter.get('distributionPeriodId')) {
						data = data.set('distributionPeriodId', rowFilter.get('distributionPeriodId'));
					}
				}
			}
		}
		return new DashboardModel(data);
	}

	async initialize(globalContext) {
		let model = await this.initializeLookups();
		model = model.initializePreferences();
		model = await model.loadData();
		model = model.#setProperty('langId', globalContext.getLanguageId());
		return model;
	}

	orderBy(columnName, desc) {
		return new DashboardModel(
			this.#data.set('sortColumn', columnName).set('sortDesc', desc)
		).applySort();
	}

	applySort(dontSavePreference) {
		const data = this.#data;
		let compare = compareBinary;
		const name = data.get('sortColumn');
		if (!name) {
			return this;
		}
		const desc = data.get('sortDesc');
		if (!dontSavePreference) {
			setPreference(this.#getOrderByPreferenceKey(), Immutable.Map({
				sortColumn: name,
				sortDesc: desc
			}));
		}
		if (name === 'categoryName') {
			compare = compareStrings;
		} else if (name === 'effectivePeriodId') {
			compare = compareFirstListItem;
		}
		if (desc) {
			compare = descCompare(compare);
		}
		let rows = orderBy(data.get('rows'), name, compare);
		return this.#setProperty('rows', rows).applyFilter(dontSavePreference);
	}

	updateEffectivePeriodLookup() {
		const rows = this.#data.get('rows');
		if (!rows) {
			return this;
		}
		let epis = [];
		rows.forEach(row => {
			const epi = row.get('effectivePeriodId');
			if (!epi || epi.size === 0) {
				return;
			}
			epi.forEach(dpId => {
				if (epis.indexOf(dpId) < 0) {
					epis.push(dpId);
				}
			});
		});
		epis.sort();
		return this.#setProperty('effectivePeriodLookup',
			StaticLookup(Immutable.fromJS(
				epis.map(key => {
					return {
						key,
						cells: [key]
					};
				})
			))
		);
	}

	getEffectivePeriodLookup() {
		return this.#data.get('effectivePeriodLookup') || EMPTY_LOOKUP;
	}

	getExpandedRows() {
		return this.#data.get('expandedRows') || EMPTY_SET;
	}

	getSortColumn() {
		return this.#data.get('sortColumn');
	}

	getSortDesc() {
		return this.#data.get('sortDesc');
	}

	getFilter() {
		return this.#data.get('filter') || EMPTY_MAP;
	}

	getRoleId() {
		return this.#data.get('roleId');
	}

	getRoleLookup() {
		return this.#data.get('roleLookup');
	}

	getDistributionPeriodId() {
		return this.#data.get('distributionPeriodId');
	}

	getDistributionPeriodLookup() {
		return this.#data.get('distributionPeriodLookup.' + this.getRoleId());
	}

	getSummary() {
		return this.#data.get('summary') || EMPTY_MAP;
	}

	getFilteredSummary() {
		return this.#data.get('filteredSummary') || EMPTY_MAP;
	}

	getFilteredRows() {
		const data = this.#data;
		return data.get('filteredRows') || data.get('rows') || EMPTY_LIST;
	}

	setFilterItem(name, value) {
		//console.log('sfi', name, value);
		return this.#setProperty(['filter', name], value).applyFilter();
	}

	applyFilter(dontSavePreference) {
		const filter = this.#data.get('filter') || EMPTY_MAP;
		if (!dontSavePreference) {
			setPreference(this.#getFilterPreferenceKey(), filter);
		}
		let matchers = [];
		let filterMask = 1;
		matcherDefinitions.forEach(md => {
			const value = filter.get(md.name);
			if (!isEmpty(value)) {
				const matcher = md.matcher(md.name, value);
				if (md.name === 'categoryName') {
					matcher.filterMask = filterMask;
				} else {
					matcher.filterMask = 0;
				}
				matchers.push(matcher);
				filterMask <<= 1;
			}
		});
		if (matchers.length === 0) {
			return this.#setProperty('filteredRows', null).#setProperty('filteredSummary', null);
		} else {
			let rows = filterRows(this.#data.get('rows'), matchers);
			let filteredSummary = sumChildAmounts(EMPTY_MAP.set('children', rows));
			return this.#setProperty('filteredRows', rows).#setProperty('filteredSummary', filteredSummary);
		}
	}

	async loadData() {
		const data = this.#data;
		const partyRole = parseRoleId(data.get('roleId'));
		if (partyRole) {
			setPreference(this.#getRowFilterPreferenceKey(), Immutable.Map({
				roleId: data.get('roleId'),
				distributionPeriodId: data.get('distributionPeriodId')
			}));
		} else {
			return this;
		}

		const res = await webapi.execute('/api/proc/api_dashboard/getrows', {
			partyId: partyRole.partyId,
			roleTypeId: partyRole.roleTypeId,
			distributionPeriodId: data.get('distributionPeriodId')
		});
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		return this
			.#setProperty('summary', Immutable.fromJS(processSummary(res.summary)))
			.#setProperty('rows', Immutable.fromJS(processRows(res.rows || [])))
			.updateEffectivePeriodLookup()
			.applySort(true)
			.applyFilter(true);
	}

	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		let dp = this.#data.get('distributionPeriodLookup.' + roleId);
		if (dp) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

function equalityMatcher(name, value) {
	return function (row) {
		return row.get(name) === value;
	};
}

function reverseInMatcher(name, value) {
	return function (row) {
		const rowValue = row.get(name);
		if (!rowValue) {
			return false;
		}
		if (rowValue.size) {
			return rowValue.some(item => item === value);
		} else {
			return rowValue === value;
		}
	};
}

function inRangeMatcher(name, value) {
	value = value || EMPTY_MAP;
	const filterFrom = value.get('from') ?? null;
	const filterTo = value.get('to') ?? null;
	return function (row) {
		const rowValue = row.get(name);
		if (!rowValue) {
			return false;
		}
		if (filterFrom != null && filterFrom > rowValue) {
			return false;
		}
		if (filterTo != null && filterTo < rowValue) {
			return false;
		}
		return true;
	};
}

function srchStrMatcher(name, value) {
	name += 'Srch';
	const tokens = getSrchTokens(value);
	return function (row) {
		const value = row.get(name);
		if (!value) {
			return false;
		}
		return matchSrchTokens(tokens, value);
	};
}

const matcherDefinitions = [
	{ name: 'categoryName', matcher: srchStrMatcher },
	{ name: 'distType', matcher: equalityMatcher },
	{ name: 'effectivePeriodId', matcher: reverseInMatcher },
	{ name: 'brutto', matcher: inRangeMatcher },
	{ name: 'netto', matcher: inRangeMatcher }
];

function sumChildAmounts(row) {
	let kids = row.get('children');
	if (kids && kids.size > 0) {
		let netto = 0;
		let brutto = 0;
		kids.forEach(kid => {
			netto += kid.get('netto');
			brutto += kid.get('brutto');
		});
		row = row.set('netto', netto).set('brutto', brutto);
	}
	return row;
}

function filterRow(row, matchers, matchedMask) {
	let kids = row.get('children');
	let selfNotMatched = false;
	for (let i = 0; i < matchers.length; i++) {
		let matcher = matchers[i];
		if (matcher.filterMask & matchedMask) {
			continue;
		}
		let isMatched = matcher(row);
		if (isMatched) {
			if (matcher.filterMask) {
				matchedMask |= matcher.filterMask;
			}
		} else if (!kids) {
			return null;
		} else {
			selfNotMatched = true;
		}
	}
	if (kids) {
		kids = filterRows(kids, matchers, matchedMask);
		if (kids.size > 0 || !selfNotMatched) {
			return sumChildAmounts(row.set('children', kids.size > 0 ? kids : null));
		} else {
			return null;
		}
	} else {
		return row;
	}

}

function filterRows(rows, matchers, matchedMask = 0) {
	if (!rows) {
		return null;
	}
	return rows.map(row => filterRow(row, matchers, matchedMask)).filter(Boolean);
}

function compareStrings(a, b) {
	if (a) {
		if (b) {
			return a.localeCompare(b);
		} else {
			return -1;
		}
	} else {
		if (b) {
			return 1;
		} else {
			return 0;
		}
	}
}

function compareBinary(a, b) {
	return a < b ? -1 : a > b ? 1 : 0;
}

function compareFirstListItem(a, b) {
	return compareBinary((a || EMPTY_LIST).get(0), (b || EMPTY_LIST).get(0));
}

function descCompare(func) {
	return function (a, b) {
		return -func(a, b);
	};
}

function orderBy(rows, name, compare) {
	rows = rows.sort((a, b) => {
		let res = compare(a.get(name), b.get(name));
		if (res === 0) {
			res = compareBinary(a.get('naturalOrder'), b.get('naturalOrder'));
		}
		return res;
	});
	rows = rows.map(row => {
		const kids = row.get('children');
		if (kids) {
			row = row.set('children', orderBy(kids, name, compare));
		}
		return row;
	});
	return rows;
}

export default DashboardModel;
